import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  CardActionArea,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  InputAdornment,
  IconButton,
  Alert,
  AlertTitle,
  Snackbar,
  Tabs,
  Tab,
  CircularProgress,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import BoltIcon from '@mui/icons-material/Bolt';
import GroupIcon from '@mui/icons-material/Group';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WarningIcon from '@mui/icons-material/Warning';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DescriptionIcon from '@mui/icons-material/Description';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';
import { useApp } from '../../context/AppContext';
import { SELECTED_APP_ID_KEY, SELECTED_APP_STAFF_KEY, SELECTED_APP_NAME_KEY, SELECTED_APP_USER_HAS_ACCESS_KEY, STAFF_DEPARTMENT_IDS_KEY } from '../shared/SelectAppPage';
import { useNavigate, useLocation } from 'react-router-dom';
import { formatDate } from '../../utils/dateFormat';
import { getApiUrl } from '../../utils/apiUrl';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ForwardIcon from '@mui/icons-material/Forward';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

const ReporterDashboard = () => {
  const { currentUser, addSupplementaryInfo } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [openSupplementaryDialog, setOpenSupplementaryDialog] = useState(false);
  const [supplementaryItem, setSupplementaryItem] = useState(null);
  const [supplementaryText, setSupplementaryText] = useState('');
  const [supplementaryImages, setSupplementaryImages] = useState([]);
  const [tablePage, setTablePage] = useState(0);
  const [tableRowsPerPage, setTableRowsPerPage] = useState(10);
  const [openDeleteConfirmDialog, setOpenDeleteConfirmDialog] = useState(false);
  const [itemToDeleteId, setItemToDeleteId] = useState(null);
  const deleteConfirmCancelRef = React.useRef(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState({ severity: 'info', title: '', text: '' });
  const [announcements, setAnnouncements] = useState([]);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [imagePreviewUrls, setImagePreviewUrls] = useState([]);
  const [imagePreviewIndex, setImagePreviewIndex] = useState(0);
  const [documentPickerOpen, setDocumentPickerOpen] = useState(false);
  const [documentPickerFiles, setDocumentPickerFiles] = useState([]);
  const [ticketList, setTicketList] = useState([]);
  const [ticketCount, setTicketCount] = useState(0);
  const [ticketLoading, setTicketLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [authVerifyUser, setAuthVerifyUser] = useState(null);
  const [announcementsLoading, setAnnouncementsLoading] = useState(true);
  const [departmentsLoading, setDepartmentsLoading] = useState(true);
  const [authVerifyLoading, setAuthVerifyLoading] = useState(true);
  const [displayAppName, setDisplayAppName] = useState(null);

  const myHelpDeskList = ticketList;
  const filteredHelpDeskList = myHelpDeskList.filter((item) => {
    const matchSearch = !searchQuery.trim() ||
      [item.title, item.description || '', item.location || ''].some(
        (s) => String(s).toLowerCase().includes(searchQuery.toLowerCase())
      );
    const matchCategory = !filterCategory || String(item.category) === filterCategory;
    const matchStatus = !filterStatus || item.status === filterStatus;
    return matchSearch && matchCategory && matchStatus;
  });
  const activeNotifications = useMemo(
    () =>
      announcements.map((a) => ({
        ...a,
        departmentName: departments.find((d) => d.id === a.department)?.name || '',
      })),
    [announcements, departments]
  );

  // แปลงสถานะจาก API เป็นภาษาไทย (ค่าจาก API ไม่ใช้ types.js)
  // สถานะมี 4 แบบเท่านั้น: Pending, In Progress, Completed, Cancelled
  const STATUS_THAI = useMemo(() => ({
    pending: 'รอรับเรื่อง',
    forward: 'ส่งต่อ',
    in_progress: 'กำลังดำเนินการ (รับเรื่องแล้ว)',
    completed: 'ดำเนินการเสร็จสิ้น',
    cancelled: 'ยกเลิก',
  }), []);
  const statusToThai = (status) => STATUS_THAI[status] ?? status ?? '-';
  const URGENCY_THAI = useMemo(() => ({
    normal: 'ปกติ',
    high: 'เร่งด่วน',
  }), []);
  const urgencyToThai = (urgency) => URGENCY_THAI[urgency] ?? URGENCY_THAI.normal;

  // สีและไอคอนสถานะ (คำนวณครั้งเดียวแล้ว lookup ตาม status — ไม่สร้าง object/icon ซ้ำทุกแถว)
  const STATUS_DISPLAY = useMemo(() => {
    const fallback = { bgcolor: 'rgba(0, 0, 0, 0.04)', color: 'rgba(0, 0, 0, 0.6)', icon: null };
    return {
      pending: { bgcolor: 'rgba(237, 108, 2, 0.1)', color: '#ed6c02', icon: <ScheduleIcon sx={{ fontSize: 16, color: 'inherit' }} /> },
      forward: { bgcolor: 'rgba(2, 119, 189, 0.1)', color: '#0277bd', icon: <ForwardIcon sx={{ fontSize: 16, color: 'inherit' }} /> },
      in_progress: { bgcolor: 'rgba(132, 94, 194, 0.1)', color: '#845EC2', icon: <AutorenewIcon sx={{ fontSize: 16, color: 'inherit' }} /> },
      completed: { bgcolor: 'rgba(46, 125, 50, 0.1)', color: '#2e7d32', icon: <CheckCircleIcon sx={{ fontSize: 16, color: 'inherit' }} /> },
      cancelled: { bgcolor: 'rgba(0, 0, 0, 0.06)', color: 'rgba(0, 0, 0, 0.6)', icon: <CancelIcon sx={{ fontSize: 16, color: 'inherit' }} /> },
      _fallback: fallback,
    };
  }, []);
  const getStatusDisplay = (status) => STATUS_DISPLAY[status] ?? STATUS_DISPLAY._fallback;
  const getUrgencyDisplay = (urgency) => {
    if (urgency === 'high') {
      return { bgcolor: 'rgba(211, 47, 47, 0.1)', color: '#d32f2f'  };
    }
    return { bgcolor: 'rgba(117, 117, 117, 0.12)', color: '#616161' };
  };

  useEffect(() => {
    setTablePage(0);
  }, [searchQuery, filterCategory, filterStatus]);

  useEffect(() => {
    if (imagePreviewOpen) setImagePreviewIndex(0);
  }, [imagePreviewOpen]);

  // โหลดการแจ้งเตือนจาก API: GET /api/clients/apps/{selectedAppId}/announcements/
  useEffect(() => {
    const selectedAppId = localStorage.getItem(SELECTED_APP_ID_KEY);
    const token = localStorage.getItem('token');
    const appsBase = getApiUrl('/api/clients/apps/').replace(/\/$/, '');
    if (!selectedAppId || !appsBase) {
      setAnnouncements([]);
      setAnnouncementsLoading(false);
      return;
    }
    setAnnouncementsLoading(true);
    const url = `${appsBase}/${selectedAppId}/announcements/`;
    const headers = token ? { Authorization: `Token ${token}` } : {};
    axios
      .get(url, { headers })
      .then(({ data }) => {
        const results = data?.results ?? (Array.isArray(data) ? data : []);
        setAnnouncements(results);
      })
      .catch(() => setAnnouncements([]))
      .finally(() => setAnnouncementsLoading(false));
  }, []);

  // โหลดรายการ department จาก /api/clients/apps/{selectedAppId}/departments/
  useEffect(() => {
    const selectedAppId = localStorage.getItem(SELECTED_APP_ID_KEY);
    const appsBase = getApiUrl('/api/clients/apps/').replace(/\/$/, '');
    if (!selectedAppId || !appsBase) {
      setDepartments([]);
      setDepartmentsLoading(false);
      return;
    }
    setDepartmentsLoading(true);
    const url = `${appsBase}/${selectedAppId}/departments/`;
    axios.get(url)
      .then(({ data }) => {
        const list = data?.results ?? (Array.isArray(data) ? data : []);
        setDepartments(list.map((d) => ({ id: d.id, name: d.name || '', description: d.description || '' })));
      })
      .catch(() => setDepartments([]))
      .finally(() => setDepartmentsLoading(false));
  }, []);

  // โหลดชื่อแอปสำหรับแสดง (จาก localStorage หรือ API)
  useEffect(() => {
    const selectedAppId = localStorage.getItem(SELECTED_APP_ID_KEY);
    const fromStorage = localStorage.getItem(SELECTED_APP_NAME_KEY);
    if (fromStorage) {
      setDisplayAppName(fromStorage);
      return;
    }
    if (!selectedAppId) {
      setDisplayAppName(null);
      return;
    }
    const appsBase = getApiUrl('/api/clients/apps/').replace(/\/$/, '');
    if (!appsBase) {
      setDisplayAppName(null);
      return;
    }
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Token ${token}` } : {};
    axios
      .get(`${appsBase}/${selectedAppId}/`, { headers })
      .then(({ data }) => {
        const name = data?.name || '';
        setDisplayAppName(name);
        if (name) localStorage.setItem(SELECTED_APP_NAME_KEY, name);
      })
      .catch(() => setDisplayAppName(null));
  }, []);

  // GET /api/user/auth/verify/ — เตรียมข้อมูล user สำหรับใช้
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setAuthVerifyLoading(false);
      return;
    }
    setAuthVerifyLoading(true);
    const url = getApiUrl('/api/user/auth/verify/');
    axios.get(url, { headers: { Authorization: `Token ${token}` } })
      .then(({ data }) => setAuthVerifyUser(data))
      .catch(() => setAuthVerifyUser(null))
      .finally(() => setAuthVerifyLoading(false));
  }, []);

  const getDepartmentDisplay = (dept) => {
    const name = dept?.name || '';
    if (/ประปา|น้ำ/i.test(name)) return { Icon: WaterDropIcon, color: '#1e90ff', borderColor: 'rgba(30, 144, 255, 0.1)', hoverBorder: 'rgba(30, 144, 255, 0.5)', hoverBg: 'rgba(30, 144, 255, 0.1)', hoverShadow: '0 8px 24px rgba(30, 144, 255, 0.3)' };
    if (/ไฟฟ้า/i.test(name)) return { Icon: BoltIcon, color: '#ff9800', borderColor: 'rgba(255, 152, 0, 0.1)', hoverBorder: 'rgba(255, 152, 0, 0.5)', hoverBg: 'rgba(255, 152, 0, 0.1)', hoverShadow: '0 8px 24px rgba(255, 152, 0, 0.3)' };
    return { Icon: GroupIcon, color: '#845EC2', borderColor: 'rgba(132, 94, 194, 0.1)', hoverBorder: 'rgba(132, 94, 194, 0.5)', hoverBg: 'rgba(132, 94, 194, 0.1)', hoverShadow: '0 8px 24px rgba(132, 94, 194, 0.3)' };
  };

  const getCategoryDisplay = (departmentName) => getDepartmentDisplay({ name: departmentName });

  const selectedAppId = typeof window !== 'undefined' ? localStorage.getItem(SELECTED_APP_ID_KEY) : null;

  const fetchTickets = useCallback(() => {
    const base = getApiUrl('/api/tickets/mine/');
    const token = localStorage.getItem('token');
    if (!base || !token || !selectedAppId) {
      setTicketList([]);
      setTicketCount(0);
      return Promise.resolve();
    }
    setTicketLoading(true);
    const apiPage = tablePage + 1;
    const url = `${base.replace(/\/$/, '')}/by-app/${selectedAppId}/`;
    const mapOne = (r) => ({
      id: r.id,
      title: r.title,
      description: r.description,
      department: r.department,
      department_name: r.department_name,
      category: r.department,
      location: r.location,
      telephone: r.telephone,
      address: r.address,
      urgency: r.urgency || r.priority || 'normal',
      status: r.status || 'pending',
      createdAt: r.created_at,
      updatedAt: r.updated_at,
      owner: r.owner,
      owner_username: r.owner_username,
      owner_fullname: r.owner_fullname,
    });
    return axios
      .get(url, {
        params: { page: apiPage, page_size: tableRowsPerPage },
        headers: { Authorization: `Token ${token}` },
      })
      .then(({ data }) => {
        const results = Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
        setTicketList(results.map(mapOne));
        setTicketCount(data?.count ?? results.length);
      })
      .catch(() => {
        setTicketList([]);
        setTicketCount(0);
      })
      .finally(() => setTicketLoading(false));
  }, [tablePage, tableRowsPerPage, selectedAppId]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const paginatedHelpDeskList = filteredHelpDeskList;

  const renderNotificationAttachmentIndicators = (notification) => {
    const attachments = Array.isArray(notification?.attachments) ? notification.attachments : [];
    const isImage = (a) => /\.(png|jpe?g|gif|webp)(\?|$)/i.test(String(a?.file || ''));
    const imageAttachments = attachments.filter(isImage);
    const imageCount = imageAttachments.length;
    const fileCount = attachments.filter((a) => !isImage(a)).length;
    const activeColor = '#845EC2';

    const handleImageClick = () => {
      if (imageCount > 0) {
        setImagePreviewUrls(imageAttachments.map((a) => a.file).filter(Boolean));
        setImagePreviewOpen(true);
      }
    };

    const fileAttachments = attachments.filter((a) => !isImage(a));
    const handleFileClick = () => {
      if (fileCount === 0) return;
      if (fileCount > 1) {
        setDocumentPickerFiles(fileAttachments.filter((a) => a?.file));
        setDocumentPickerOpen(true);
      } else if (fileAttachments[0]?.file) {
        window.open(fileAttachments[0].file, '_blank', 'noopener,noreferrer');
      }
    };

    if (imageCount === 0 && fileCount === 0) return null;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mt: 0.5 }}>
        {imageCount > 0 && (
          <Box
            onClick={handleImageClick}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              color: activeColor,
              cursor: 'pointer',
              fontSize: '0.8125rem',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            <ImageIcon sx={{ fontSize: 18 }} />
            <Typography component="span" variant="caption" sx={{ color: 'inherit' }}>
              รูปภาพ {imageCount} รูป
            </Typography>
          </Box>
        )}
        {fileCount > 0 && (
          <Box
            onClick={handleFileClick}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              color: activeColor,
              cursor: 'pointer',
              fontSize: '0.8125rem',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            <DescriptionIcon sx={{ fontSize: 18 }} />
            <Typography component="span" variant="caption" sx={{ color: 'inherit' }}>
              ไฟล์เอกสาร {fileCount} ไฟล์
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  const renderHelpDeskAttachmentIndicators = (item) => {
    const imageCount = Array.isArray(item?.images) ? item.images.length : 0;
    const fileCount = Array.isArray(item?.attachments) ? item.attachments.length : 0;
    const activeColor = '#845EC2';

    if (imageCount === 0 && fileCount === 0) return null;

    return (
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.6,
          ml: 0.75,
          verticalAlign: 'middle',
          whiteSpace: 'nowrap',
        }}
      >
        {imageCount > 0 && (
          <Box component="span" sx={{ display: 'inline-flex', color: activeColor }}>
            <ImageIcon sx={{ fontSize: 18 }} />
          </Box>
        )}
        {fileCount > 0 && (
          <Box component="span" sx={{ display: 'inline-flex', color: activeColor }}>
            <DescriptionIcon sx={{ fontSize: 18 }} />
          </Box>
        )}
      </Box>
    );
  };

  useEffect(() => {
    if (location.state?.createSuccess) {
      setSnackbarMessage({ severity: 'success', title: 'สร้างรายการสำเร็จ', text: 'รายการแจ้งข้อขัดข้องถูกสร้างแล้ว' });
      setSnackbarOpen(true);
      navigate(location.pathname + location.search, { replace: true, state: {} });
    }
  }, [location.state?.createSuccess]);

  const supplementaryImageInputRef = React.useRef(null);
  const handleSupplementaryImageChange = (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    const maxFiles = 5;
    const maxSizeMB = 3;
    const remaining = maxFiles - (supplementaryImages?.length || 0);
    const toAdd = Array.from(files).slice(0, remaining);
    toAdd.forEach((file) => {
      if (file.size > maxSizeMB * 1024 * 1024) {
        setSnackbarMessage({ severity: 'warning', title: 'ไฟล์ใหญ่เกินขนาด', text: `ไฟล์ ${file.name} ใหญ่เกิน ${maxSizeMB} MB` });
        setSnackbarOpen(true);
        return;
      }
      const reader = new FileReader();
      reader.onload = (ev) => {
        setSupplementaryImages((prev) => [...(prev || []), { name: file.name, dataUrl: ev.target.result }]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };
  const handleRemoveSupplementaryImage = (index) => {
    setSupplementaryImages((prev) => (prev || []).filter((_, i) => i !== index));
  };

  const hasSelectedApp = typeof localStorage !== 'undefined' && !!localStorage.getItem(SELECTED_APP_ID_KEY);

  return (
    <Box sx={{ px: { xs: 2, sm: 0 }, pb: { xs: 3, sm: 0 } }}>
      <>
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          {hasSelectedApp && (
            <Typography variant="h6" sx={{ color: 'rgba(0, 0, 0, 0.6)', mb: 1.5, fontWeight: 600, textAlign: 'center' }}>
              APP : {displayAppName != null ? (displayAppName || '-') : 'กำลังโหลด...'}
            </Typography>
          )}
          <Box
            sx={{
              display: 'flex',
              justifyContent: { xs: 'center', sm: 'space-between' },
              alignItems: 'center',
              flexDirection: { xs: 'column', sm: 'row' },
              flexWrap: 'wrap',
              gap: { xs: 1.25, sm: 2 },
              mb: 2,
              width: { xs: '100%', sm: 'auto' }
            }}
          >
            <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.5)', textAlign: 'left', width: { xs: '100%', sm: 'auto' }, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', fontSize: '0.8125rem' }}>
              {authVerifyLoading ? (
                <>
                  <CircularProgress size={18} thickness={4} sx={{ color: '#845EC2' }} />
                  <span>กำลังโหลด...</span>
                </>
              ) : (
                <>
                  ยินดีต้อนรับ,{' '}
                  <Box component="span" sx={{ display: { xs: 'block', sm: 'inline' } }}>
                    {authVerifyUser?.full_name}
                  </Box>
                </>
              )}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, flexShrink: 0, alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: { xs: 'stretch', sm: 'flex-start' }, width: { xs: '100%', sm: 'auto' } }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ArrowBackIcon />}
                onClick={() => {
                  localStorage.removeItem(SELECTED_APP_ID_KEY);
                  localStorage.removeItem(SELECTED_APP_STAFF_KEY);
                  localStorage.removeItem(SELECTED_APP_NAME_KEY);
                  localStorage.removeItem(SELECTED_APP_USER_HAS_ACCESS_KEY);
                  localStorage.removeItem(STAFF_DEPARTMENT_IDS_KEY);
                  navigate('/select-app');
                }}
                sx={{
                  borderRadius: 1,
                  minHeight: { xs: 44, sm: 36 },
                  width: { xs: '100%', sm: 'auto' },
                  borderColor: 'rgba(132, 94, 194, 0.5)',
                  color: '#845EC2',
                  '&:hover': { borderColor: '#845EC2', bgcolor: 'rgba(132, 94, 194, 0.08)' }
                }}
              >
                ย้อนกลับไปเลือกแอป
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Notifications Section - อยู่ภายใน card เดียว */}
        {announcementsLoading && (
          <Box sx={{ mb: 4 }}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 2,
                background: 'linear-gradient(to left, rgb(185, 224, 242), rgba(229, 213, 255, 0.8))',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                overflow: 'hidden',
              }}
            >
              <CardContent sx={{ py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={44} thickness={3.6} sx={{ color: '#845EC2' }} />
                <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.5)' }}>กำลังโหลดการแจ้งเตือน...</Typography>
              </CardContent>
            </Card>
          </Box>
        )}
        {!announcementsLoading && (
          <Box sx={{ mb: 4 }}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 2,
                background: 'linear-gradient(to left, rgb(185, 224, 242), rgba(229, 213, 255, 0.8))',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                overflow: 'hidden',
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 600, color: 'rgba(0, 0, 0, 0.6)', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      animation: 'notificationIconShake 2s ease-in-out infinite',
                      '@keyframes notificationIconShake': {
                        '0%, 100%': { transform: 'translateX(0) rotate(0deg)' },
                        '10%, 30%': { transform: 'translateX(-2px) rotate(-8deg)' },
                        '20%, 40%': { transform: 'translateX(2px) rotate(8deg)' },
                        '50%': { transform: 'translateX(0) rotate(0deg)' },
                      },
                    }}
                  >
                    <WarningIcon sx={{ color: '#ed6c02', fontSize: 26 }} />
                  </Box>
                  การแจ้งเตือน
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {activeNotifications.length === 0 ? (
                    <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.5)', py: 1 }}>
                      ไม่มีการประกาศ
                    </Typography>
                  ) : activeNotifications.map((notification) => {
                    const deptName = notification.departmentName || 'ประกาศ';
                    const isWater = /ประปา|น้ำ/i.test(deptName);
                    const isElectric = /ไฟฟ้า/i.test(deptName);
                    const accent = isWater ? '#1e90ff' : isElectric ? '#ed6c02' : '#845EC2';
                    const text = notification.description || notification.title || '';
                    const start = notification.operation_start ? formatDate(notification.operation_start) : '';
                    const end = notification.operation_end ? formatDate(notification.operation_end) : '';
                    const dateRange = start && end ? `เริ่ม ${start} – เสร็จ ${end}` : (start ? `เริ่ม ${start}` : '');
                    return (
                      <Box
                        key={notification.id}
                        sx={{
                          py: 1.5,
                          px: 2,
                          borderRadius: 1.5,
                          bgcolor: 'rgba(0, 0, 0, 0.02)',
                          border: '1px solid rgba(0, 0, 0, 0.06)',
                          transition: 'border-color 0.2s, bgcolor 0.2s',
                          '&:hover': {
                            borderColor: alpha(accent, 0.2),
                            bgcolor: alpha(accent, 0.04),
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1.5, flexWrap: 'wrap' }}>
                          <Box
                            sx={{
                              width: 40,
                              minHeight: 40,
                              borderRadius: 2,
                              bgcolor: alpha(accent, 0.12),
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            {isWater ? (
                              <WaterDropIcon sx={{ fontSize: 22, color: accent }} />
                            ) : isElectric ? (
                              <BoltIcon sx={{ fontSize: 22, color: accent }} />
                            ) : (
                              <PersonIcon sx={{ fontSize: 22, color: accent }} />
                            )}
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: accent, mb: 0.25 }}>
                              {deptName}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.7)', lineHeight: 1.5 }}>
                              {text}
                            </Typography>
                            {dateRange && (
                              <Typography variant="caption" sx={{ color: 'rgba(0, 0, 0, 0.5)', mt: 0.5, display: 'block' }}>
                                {dateRange}
                              </Typography>
                            )}
                            {renderNotificationAttachmentIndicators(notification)}
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Report Cards Section - โหลดจาก /api/clients/apps/{selectedAppId}/departments/ */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 600, color: 'rgba(0, 0, 0, 0.6)' }}>
            แจ้งข้อขัดข้อง
          </Typography>
          {departmentsLoading ? (
            <Card elevation={0} sx={{ bgcolor: 'rgba(255, 255, 255, 0.9)', backdropFilter: 'blur(10px)', borderRadius: 2, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)', border: '1px solid rgba(0, 0, 0, 0.05)' }}>
              <CardContent sx={{ py: 5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={44} thickness={3.6} sx={{ color: '#845EC2' }} />
                <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.5)' }}>กำลังโหลดหมวดหมู่...</Typography>
              </CardContent>
            </Card>
          ) : (
            <Grid container spacing={{ xs: 2, md: 3 }}>
              {departments.map((dept) => {
                const d = getDepartmentDisplay(dept);
                const Icon = d.Icon;
                return (
                  <Grid item xs={12} md={4} key={dept.id}>
                    <Card
                      elevation={0}
                      sx={{
                        height: '100%',
                        transition: 'all 0.3s ease',
                        borderRadius: 2,
                        bgcolor: 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(10px)',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                        border: `1px solid ${d.borderColor}`,
                        '&:hover': {
                          transform: 'translateY(-6px)',
                          boxShadow: d.hoverShadow,
                          border: `1px solid ${d.hoverBorder}`,
                          bgcolor: d.hoverBg,
                        }
                      }}
                    >
                      <CardActionArea
                        onClick={() => navigate(`/reporter/create?dept=${dept.id}`)}
                        sx={{ height: '100%', p: { xs: 2.25, sm: 3 } }}
                      >
                        <CardContent sx={{ textAlign: 'center' }}>
                          <Box
                            sx={{
                              width: { xs: 60, sm: 70 },
                              height: { xs: 60, sm: 70 },
                              borderRadius: '50%',
                              bgcolor: d.hoverBg,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mx: 'auto',
                              mb: 2
                            }}
                          >
                            <Icon sx={{ fontSize: { xs: 32, sm: 36 }, color: d.color }} />
                          </Box>
                          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: 'rgba(0, 0, 0, 0.6)' }}>
                            {dept.name || ''}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.5)' }}>
                            {dept.description || 'แจ้งข้อขัดข้อง'}
                          </Typography>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}

            </Grid>
          )}
        </Box>

        {/* My HelpDesk Section */}
        <Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: 2,
              mb: 2,
              flexWrap: 'wrap'
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'rgba(0, 0, 0, 0.6)', flexShrink: 0 }}>
              รายการที่เคยแจ้ง
            </Typography>
            {myHelpDeskList.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  flex: { xs: '1 1 100%', sm: '1 1 auto' },
                  gap: 1.5,
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  minWidth: 0,
                  justifyContent: { xs: 'stretch', sm: 'flex-end' }
                }}
              >
                {/*
                ทำให้ช่องค้นหา/ตัวกรองไม่เป็น "กล่องขาวทึบ"
                ให้เป็นโทน glass/pastel เข้ากับพื้นหลัง
              */}
                <TextField
                  size="small"
                  placeholder="ค้นหา เรื่อง, รายละเอียด, สถานที่..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  fullWidth={isXs}
                  sx={{
                    flex: { xs: '1 1 100%', sm: '0 1 200px' },
                    minWidth: { xs: 0, sm: 140 },
                    maxWidth: { sm: 280 },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.55)',
                      backdropFilter: 'blur(10px)',
                      '& fieldset': { borderColor: 'rgba(0,0,0,0.12)' },
                      '&:hover fieldset': { borderColor: 'rgba(132, 94, 194, 0.35)' },
                      '&.Mui-focused fieldset': { borderColor: '#845EC2' }
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: 'rgba(0, 0, 0, 0.4)', fontSize: 20 }} />
                      </InputAdornment>
                    )
                  }}
                />
                <FormControl
                  size="small"
                  fullWidth={isXs}
                  sx={{
                    minWidth: { xs: 0, sm: 100 },
                    flex: { xs: '1 1 160px', sm: '0 0 auto' },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.55)',
                      backdropFilter: 'blur(10px)',
                      '& fieldset': { borderColor: 'rgba(0,0,0,0.12)' },
                      '&:hover fieldset': { borderColor: 'rgba(132, 94, 194, 0.35)' },
                      '&.Mui-focused fieldset': { borderColor: '#845EC2' }
                    }
                  }}
                >
                  <InputLabel>ประเภท</InputLabel>
                  <Select
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                    label="ประเภท"
                  >
                    <MenuItem value="">ทั้งหมด</MenuItem>
                    {departments.map((dept) => (
                      <MenuItem key={dept.id} value={String(dept.id)}>{dept.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl
                  size="small"
                  fullWidth={isXs}
                  sx={{
                    minWidth: { xs: 0, sm: 120 },
                    flex: { xs: '1 1 160px', sm: '0 0 auto' },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.55)',
                      backdropFilter: 'blur(10px)',
                      '& fieldset': { borderColor: 'rgba(0,0,0,0.12)' },
                      '&:hover fieldset': { borderColor: 'rgba(132, 94, 194, 0.35)' },
                      '&.Mui-focused fieldset': { borderColor: '#845EC2' }
                    }
                  }}
                >
                  <InputLabel>สถานะ</InputLabel>
                  <Select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    label="สถานะ"
                  >
                    <MenuItem value="">ทั้งหมด</MenuItem>
                    {Object.keys(STATUS_THAI).map((status) => (
                      <MenuItem key={status} value={status}>{statusToThai(status)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setSearchQuery('');
                    setFilterCategory('');
                    setFilterStatus('');
                  }}
                  fullWidth={isXs}
                  sx={{
                    borderRadius: 2,
                    height: { xs: 44, sm: 40 },
                    minHeight: { xs: 44, sm: 40 },
                    flex: { xs: '1 1 100%', sm: '0 0 auto' },
                    px: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.45)',
                    backdropFilter: 'blur(10px)',
                    borderColor: 'rgba(132, 94, 194, 0.35)',
                    color: '#845EC2',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.6)',
                      borderColor: 'rgba(132, 94, 194, 0.55)'
                    }
                  }}
                >
                  ล้าง
                </Button>
              </Box>
            )}
          </Box>

          {ticketLoading && myHelpDeskList.length === 0 ? (
            <Card elevation={0} sx={{ bgcolor: 'rgba(255, 255, 255, 0.9)', backdropFilter: 'blur(10px)', borderRadius: 2, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)', border: '1px solid rgba(0, 0, 0, 0.05)' }}>
              <CardContent sx={{ py: 5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={44} thickness={3.6} sx={{ color: '#845EC2' }} />
                <Typography align="center" sx={{ color: 'rgba(0, 0, 0, 0.5)' }}>
                  กำลังโหลดรายการ...
                </Typography>
              </CardContent>
            </Card>
          ) : myHelpDeskList.length === 0 ? (
            <Card elevation={0} sx={{ bgcolor: 'rgba(255, 255, 255, 0.9)', backdropFilter: 'blur(10px)', borderRadius: 2, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)', border: '1px solid rgba(0, 0, 0, 0.05)' }}>
              <CardContent sx={{ py: 4 }}>
                <Typography align="center" sx={{ color: 'rgba(0, 0, 0, 0.5)' }}>
                  ยังไม่มีรายการที่แจ้ง
                </Typography>
              </CardContent>
            </Card>
          ) : filteredHelpDeskList.length === 0 ? (
            <Card elevation={0} sx={{ bgcolor: 'rgba(255, 255, 255, 0.9)', backdropFilter: 'blur(10px)', borderRadius: 2, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)', border: '1px solid rgba(0, 0, 0, 0.05)' }}>
              <CardContent sx={{ py: 4 }}>
                <Typography align="center" sx={{ color: 'rgba(0, 0, 0, 0.5)' }}>
                  ไม่พบรายการที่ตรงกับคำค้นหาหรือตัวกรอง
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Table view: md, lg */}
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                    border: '1px solid rgba(0, 0, 0, 0.05)'
                  }}
                >
                  <Table>
                    <TableHead>
                      <TableRow sx={{
                        background: 'linear-gradient(to right, rgb(48, 176, 235), rgba(132, 94, 194, 0.8),rgb(143, 212, 244))'
                      }}>
                        <TableCell sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }} align="center">ลำดับ</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }} align="center">เรื่อง</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }} align="center">ประเภท</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }} align="center">สถานที่เกิดเหตุ</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }} align="center">ความเร่งด่วน</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }} align="center">สถานะ</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }} align="center">สร้างเมื่อ</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }} align="center">การดำเนินการ</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedHelpDeskList.map((item, index) => (
                        <TableRow key={item.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell sx={{ color: 'rgba(0, 0, 0, 0.7)' }} align="center">{tablePage * tableRowsPerPage + index + 1}</TableCell>
                          <TableCell sx={{ color: 'rgba(0, 0, 0, 0.7)' }} align="left">
                            {item.title}
                          </TableCell>
                          <TableCell align="center">
                            {(() => {
                              const d = getCategoryDisplay(item.department_name);
                              return (
                                <Chip
                                  label={item.department_name || '-'}
                                  size="small"
                                  sx={{ bgcolor: d.hoverBg, color: d.color }}
                                />
                              );
                            })()}
                          </TableCell>
                          <TableCell sx={{ color: 'rgba(0, 0, 0, 0.7)' }} align="center">{item.location || '-'}</TableCell>
                          <TableCell align="center">
                            {(() => {
                              const u = getUrgencyDisplay(item.urgency);
                              return (
                                <Chip
                                  icon={item.urgency === 'high'
                                    ? <WarningIcon sx={{ fontSize: 16, color: 'inherit' }} />
                                    : <InfoOutlinedIcon sx={{ fontSize: 16, color: 'inherit' }} />}
                                  label={urgencyToThai(item.urgency)}
                                  size="small"
                                  sx={{
                                    fontWeight: 500,
                                    borderRadius: '9999px',
                                    bgcolor: u.bgcolor,
                                    color: u.color,
                                    '& .MuiChip-icon': { color: 'inherit' },
                                  }}
                                />
                              );
                            })()}
                          </TableCell>
                          <TableCell align="center">
                            {(() => {
                              const s = getStatusDisplay(item.status);
                              return (
                                <Chip
                                  icon={s.icon}
                                  label={statusToThai(item.status)}
                                  size="small"
                                  sx={{
                                    fontWeight: 500,
                                    borderRadius: '9999px',
                                    bgcolor: s.bgcolor,
                                    color: s.color,
                                    '& .MuiChip-icon': { color: 'inherit' },
                                  }}
                                />
                              );
                            })()}
                          </TableCell>
                          <TableCell sx={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: '0.875rem' }} align="center">
                            {formatDate(item.createdAt)}
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                              <Button
                                size="small"
                                variant="outlined"
                                title="ดูรายละเอียด"
                                onClick={() => navigate(`/ticket?id=${item.id}`)}
                                sx={{
                                  borderRadius: 2,
                                  minWidth: 40,
                                  px: 1,
                                  color: '#845EC2',
                                  borderColor: '#845EC2',
                                  '&:hover': { borderColor: '#6344A4', bgcolor: 'rgba(132, 94, 194, 0.08)' }
                                }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </Button>
                              {/* <Button
                            size="small"
                            variant="outlined"
                            title="ส่งข้อมูลเพิ่ม"
                            onClick={() => {
                              setSupplementaryItem(item);
                              setSupplementaryText('');
                              setSupplementaryImages([]);
                              setOpenSupplementaryDialog(true);
                            }}
                            sx={{
                              borderRadius: 2,
                              minWidth: 40,
                              px: 1,
                              color: '#2196f3',
                              borderColor: '#2196f3',
                              '&:hover': { borderColor: '#1976d2', bgcolor: 'rgba(33, 150, 243, 0.08)' }
                            }}
                          >
                            <InsertCommentIcon fontSize="small" />
                          </Button> */}
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                disabled={item.status !== 'pending'}
                                title="ลบ"
                                onClick={(e) => {
                                  const id = item.id;
                                  e.currentTarget?.blur();
                                  setItemToDeleteId(id);
                                  setTimeout(() => setOpenDeleteConfirmDialog(true), 50);
                                }}
                                sx={{ borderRadius: 2, minWidth: 40, px: 1 }}
                              >
                                <DeleteIcon fontSize="small" />
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={ticketCount}
                  page={tablePage}
                  onPageChange={(_, newPage) => setTablePage(newPage)}
                  rowsPerPage={tableRowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setTableRowsPerPage(parseInt(e.target.value, 10));
                    setTablePage(0);
                  }}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  labelRowsPerPage="จำนวนแถวต่อหน้า:"
                  labelDisplayedRows={({ from, to, count }) => `${from}-${to} จาก ${count}`}
                  sx={{
                    borderTop: '1px solid rgba(0,0,0,0.08)',
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                    borderRadius: '0 0 8px 8px',
                    '.MuiTablePagination-toolbar': { flexWrap: 'wrap', gap: 0.5, justifyContent: { xs: 'center', sm: 'flex-end' } }
                  }}
                />
              </Box>

              {/* Card view: xs, sm */}
              <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                <Grid container spacing={2}>
                  {paginatedHelpDeskList.map((item, index) => (
                    <Grid item xs={12} key={item.id}>
                      <Card
                        elevation={0}
                        sx={{
                          bgcolor: 'rgba(255, 255, 255, 0.9)',
                          backdropFilter: 'blur(10px)',
                          borderRadius: 2,
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                          border: '1px solid rgba(0, 0, 0, 0.05)',
                          transition: 'all 0.2s',
                          '&:hover': {
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
                            transform: 'translateY(-2px)',
                          }
                        }}
                      >
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
                            <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.5)', fontWeight: 600, minWidth: 24 }}>
                              {tablePage * tableRowsPerPage + index + 1}.
                            </Typography>
                            <Typography variant="h6" sx={{ color: 'rgba(0, 0, 0, 0.6)', fontWeight: 600, flex: 1, textAlign: 'left' }}>
                              {item.title}
                            </Typography>
                            {(() => {
                              const d = getCategoryDisplay(item.department_name);
                              return (
                                <Chip
                                  label={item.department_name || '-'}
                                  size="small"
                                  sx={{ bgcolor: d.hoverBg, color: d.color }}
                                />
                              );
                            })()}
                          </Box>
                          <Typography variant="body2" sx={{ mb: 1, color: 'rgba(0, 0, 0, 0.6)', lineHeight: 1.6 }}>
                            รายละเอียด : {item.description}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1, color: 'rgba(0, 0, 0, 0.5)', fontSize: '0.8125rem' }}>
                            สถานที่เกิดเหตุ: {item.location || '-'}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
                            {(() => {
                              const u = getUrgencyDisplay(item.urgency);
                              return (
                                <Chip
                                  icon={item.urgency === 'high'
                                    ? <WarningIcon sx={{ fontSize: 16, color: 'inherit' }} />
                                    : <InfoOutlinedIcon sx={{ fontSize: 16, color: 'inherit' }} />}
                                  label={`${urgencyToThai(item.urgency)}`}
                                  size="small"
                                  sx={{
                                    fontWeight: 500,
                                    borderRadius: '9999px',
                                    bgcolor: u.bgcolor,
                                    color: u.color,
                                    '& .MuiChip-icon': { color: 'inherit' },
                                  }}
                                />
                              );
                            })()}
                            {(() => {
                              const s = getStatusDisplay(item.status);
                              return (
                                <Chip
                                  icon={s.icon}
                                  label={statusToThai(item.status)}
                                  size="small"
                                  sx={{
                                    fontWeight: 500,
                                    borderRadius: '9999px',
                                    bgcolor: s.bgcolor,
                                    color: s.color,
                                    '& .MuiChip-icon': { color: 'inherit' },
                                  }}
                                />
                              );
                            })()}
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: { xs: 'nowrap', sm: 'wrap' } }}>
                            <Button
                              size="small"
                              variant="outlined"
                              title="ดูรายละเอียด"
                              onClick={() => navigate(`/ticket?id=${item.id}`)}
                              startIcon={isXs ? <VisibilityIcon fontSize="small" /> : undefined}
                              sx={{
                                borderRadius: 1,
                                minWidth: { xs: 0, sm: 40 },
                                px: { xs: 1.25, sm: 1 },
                                minHeight: { xs: 44, sm: 32 },
                                flex: { xs: '1 1 0', sm: '0 0 auto' },
                                color: '#845EC2',
                                borderColor: '#845EC2',
                                '&:hover': { borderColor: '#6344A4', bgcolor: 'rgba(132, 94, 194, 0.08)' }
                              }}
                            >
                              {isXs ? 'ดู' : <VisibilityIcon fontSize="small" />}
                            </Button>
                            {/* <Button
                          size="small"
                          variant="outlined"
                          title="ส่งข้อมูลเพิ่ม"
                          onClick={() => {
                            setSupplementaryItem(item);
                            setSupplementaryText('');
                            setSupplementaryImages([]);
                            setOpenSupplementaryDialog(true);
                          }}
                          startIcon={isXs ? <InsertCommentIcon fontSize="small" /> : undefined}
                          sx={{
                            borderRadius: 1,
                            minWidth: { xs: 0, sm: 40 },
                            px: { xs: 1.25, sm: 1 },
                            minHeight: { xs: 44, sm: 32 },
                            flex: { xs: '1 1 0', sm: '0 0 auto' },
                            color: '#2196f3',
                            borderColor: '#2196f3',
                            '&:hover': { borderColor: '#1976d2', bgcolor: 'rgba(33, 150, 243, 0.08)' }
                          }}
                        >
                          {isXs ? 'เพิ่ม' : <InsertCommentIcon fontSize="small" />}
                        </Button> */}
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              disabled={item.status !== 'pending'}
                              title="ลบ"
                              onClick={(e) => {
                                const id = item.id;
                                e.currentTarget?.blur();
                                setItemToDeleteId(id);
                                setTimeout(() => setOpenDeleteConfirmDialog(true), 50);
                              }}
                              startIcon={isXs ? <DeleteIcon fontSize="small" /> : undefined}
                              sx={{
                                borderRadius: 1,
                                minWidth: { xs: 0, sm: 40 },
                                px: { xs: 1.25, sm: 1 },
                                minHeight: { xs: 44, sm: 32 },
                                flex: { xs: '1 1 0', sm: '0 0 auto' }
                              }}
                            >
                              {isXs ? 'ลบ' : <DeleteIcon fontSize="small" />}
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
                <TablePagination
                  component="div"
                  count={ticketCount}
                  page={tablePage}
                  onPageChange={(_, newPage) => setTablePage(newPage)}
                  rowsPerPage={tableRowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setTableRowsPerPage(parseInt(e.target.value, 10));
                    setTablePage(0);
                  }}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  labelRowsPerPage="จำนวนแถวต่อหน้า:"
                  labelDisplayedRows={({ from, to, count }) => `${from}-${to} จาก ${count}`}
                  sx={{
                    borderTop: '1px solid rgba(0,0,0,0.08)',
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                    borderRadius: 2,
                    '.MuiTablePagination-toolbar': { flexWrap: 'wrap', gap: 0.5, justifyContent: { xs: 'center', sm: 'flex-end' } }
                  }}
                />
              </Box>
            </>
          )}
        </Box>

        {/* Snackbar + Alert แบบหน้า Login */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarMessage.severity}
            sx={{ width: '100%', fontSize: '0.875rem' }}
          >
            {snackbarMessage.title && <AlertTitle sx={{ fontSize: '0.875rem' }}>{snackbarMessage.title}</AlertTitle>}
            {snackbarMessage.text}
          </Alert>
        </Snackbar>

        {/* Popup แสดงรูป - ตั้งแต่ 2 ไฟล์ขึ้นไป มี tab แบบ Shopee */}
        <Dialog
          open={imagePreviewOpen}
          onClose={() => setImagePreviewOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle sx={{ pb: 0, fontWeight: 600, color: 'rgba(0,0,0,0.7)' }}>
            รูปภาพแนบ
          </DialogTitle>
          {imagePreviewUrls.length >= 2 ? (
            <>
              <Tabs
                value={Math.min(imagePreviewIndex, imagePreviewUrls.length - 1)}
                onChange={(_, v) => setImagePreviewIndex(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  borderBottom: '1px solid rgba(0,0,0,0.08)',
                  minHeight: 48,
                  px: 1,
                  '& .MuiTab-root': { minHeight: 48, textTransform: 'none', fontWeight: 600 },
                }}
              >
                {imagePreviewUrls.map((_, i) => (
                  <Tab key={i} label={`รูป ${i + 1}`} id={`image-tab-${i}`} aria-controls={`image-panel-${i}`} />
                ))}
              </Tabs>
              <DialogContent sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 280 }}>
                {imagePreviewUrls[Math.min(imagePreviewIndex, imagePreviewUrls.length - 1)] && (
                  <img
                    src={imagePreviewUrls[Math.min(imagePreviewIndex, imagePreviewUrls.length - 1)]}
                    alt={`รูป ${imagePreviewIndex + 1}`}
                    style={{ maxWidth: '100%', maxHeight: '65vh', objectFit: 'contain' }}
                  />
                )}
              </DialogContent>
            </>
          ) : imagePreviewUrls.length === 1 ? (
            <DialogContent sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
              <img
                src={imagePreviewUrls[0]}
                alt="รูป 1"
                style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
              />
            </DialogContent>
          ) : (
            <DialogContent sx={{ p: 2, textAlign: 'center', color: 'rgba(0,0,0,0.5)' }}>
              <Typography>ไม่มีรูปภาพ</Typography>
            </DialogContent>
          )}
          <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
            <Button onClick={() => setImagePreviewOpen(false)} variant="contained" sx={{ borderRadius: 2 }}>
              ปิด
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog เลือกไฟล์เอกสาร (เมื่อมีมากกว่า 1 ไฟล์) */}
        <Dialog
          open={documentPickerOpen}
          onClose={() => setDocumentPickerOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle sx={{ fontWeight: 600 }}>เลือกไฟล์ที่จะเปิด</DialogTitle>
          <DialogContent>
            <List dense disablePadding>
              {documentPickerFiles.map((att, i) => {
                const fileUrl = att?.file || '';
                const raw = att?.name || (typeof fileUrl === 'string' ? fileUrl.split('/').filter(Boolean).pop() : '') || '';
                let fileName = `ไฟล์ ${i + 1}`;
                if (raw) {
                  try {
                    fileName = decodeURIComponent(raw);
                  } catch (_) {
                    fileName = raw;
                  }
                }
                return (
                  <ListItem
                    key={i}
                    button
                    onClick={() => {
                      if (fileUrl) window.open(fileUrl, '_blank', 'noopener,noreferrer');
                      setDocumentPickerOpen(false);
                    }}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      '&:hover': { bgcolor: 'rgba(132, 94, 194, 0.08)' },
                    }}
                  >
                    <DescriptionIcon sx={{ color: '#845EC2', fontSize: 22, mr: 1.5 }} />
                    <ListItemText
                      primary={fileName}
                      primaryTypographyProps={{ fontWeight: 500, color: 'rgba(0,0,0,0.8)' }}
                    />
                    <ArrowForwardIcon sx={{ fontSize: 18, color: 'rgba(0,0,0,0.4)' }} />
                  </ListItem>
                );
              })}
            </List>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setDocumentPickerOpen(false)} sx={{ borderRadius: 1 }}>
              ปิด
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog ยืนยันลบ — blur ปุ่มลบก่อนเปิด + โฟกัสปุ่มยกเลิกใน onEntered เพื่อแก้ aria-hidden */}
        <Dialog
          open={openDeleteConfirmDialog}
          onClose={() => { setOpenDeleteConfirmDialog(false); setItemToDeleteId(null); }}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
          TransitionProps={{ onEntered: () => deleteConfirmCancelRef.current?.focus() }}
        >
          <DialogTitle id="delete-confirm-dialog-title">ยืนยันการลบ</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mt: 0.5 }}>
              <AlertTitle sx={{ fontSize: '0.9375rem' }}>ต้องการลบรายการนี้ใช่หรือไม่?</AlertTitle>
              การลบไม่สามารถกู้คืนได้
            </Alert>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button ref={deleteConfirmCancelRef} autoFocus onClick={() => { setOpenDeleteConfirmDialog(false); setItemToDeleteId(null); }} sx={{ borderRadius: 1 }}>
              ยกเลิก
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => {
                if (itemToDeleteId) {
                  const base = getApiUrl('/api/tickets/');
                  const deleteUrl = base ? `${base.replace(/\/$/, '')}/${itemToDeleteId}/` : null;
                  if (deleteUrl) {
                    axios.delete(deleteUrl)
                      .then(() => fetchTickets())
                      .catch(() => { });
                  }
                  setOpenDeleteConfirmDialog(false);
                  setItemToDeleteId(null);
                }
              }}
              sx={{ borderRadius: 1 }}
            >
              ลบ
            </Button>
          </DialogActions>
        </Dialog>

        {/* ส่งข้อมูลเพิ่มเติม Dialog */}
        <Dialog
          open={openSupplementaryDialog}
          onClose={() => {
            setOpenSupplementaryDialog(false);
            setSupplementaryItem(null);
            setSupplementaryText('');
            setSupplementaryImages([]);
          }}
          maxWidth="sm"
          fullWidth
          fullScreen={isXs}
          PaperProps={{ sx: { borderRadius: { xs: 0, sm: 2 } } }}
        >
          <DialogTitle>ส่งข้อมูลเพิ่มเติม</DialogTitle>
          <DialogContent>
            {supplementaryItem && (
              <Typography variant="body2" sx={{ mb: 1, color: 'rgba(0,0,0,0.6)' }}>
                รายการ: {supplementaryItem.title}
              </Typography>
            )}
            <TextField
              fullWidth
              multiline
              rows={4}
              label="ข้อมูลเพิ่มเติม"
              placeholder="พิมพ์ข้อมูลเพิ่มเติมที่ต้องการแจ้ง..."
              value={supplementaryText}
              onChange={(e) => setSupplementaryText(e.target.value)}
              sx={{ mt: 1 }}
            />
            <Typography variant="body2" sx={{ mb: 1, mt: 2, color: 'text.secondary', fontWeight: 500 }}>
              รูปภาพประกอบ (ไม่เกิน 5 รูป, รูปละไม่เกิน 3 MB)
            </Typography>
            <input
              ref={supplementaryImageInputRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: 'none' }}
              onChange={handleSupplementaryImageChange}
            />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'flex-start', mb: 1 }}>
              <Button
                variant="outlined"
                component="span"
                startIcon={<AddPhotoAlternateIcon />}
                onClick={() => supplementaryImageInputRef.current?.click()}
                disabled={(supplementaryImages?.length || 0) >= 5}
                sx={{
                  borderStyle: 'dashed',
                  borderColor: 'rgba(132, 94, 194, 0.5)',
                  color: '#845EC2',
                  '&:hover': { borderColor: '#845EC2', bgcolor: 'rgba(132, 94, 194, 0.04)' }
                }}
              >
                อัปโหลดรูป
              </Button>
              {(supplementaryImages || []).map((img, i) => (
                <Box key={i} sx={{ position: 'relative', width: 72, height: 72, borderRadius: 1, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.12)', flexShrink: 0 }}>
                  <img src={img.dataUrl} alt={img.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveSupplementaryImage(i)}
                    sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'rgba(0,0,0,0.5)', color: 'white', width: 24, height: 24, '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
                  >
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => {
                setOpenSupplementaryDialog(false);
                setSupplementaryItem(null);
                setSupplementaryText('');
                setSupplementaryImages([]);
              }}
              sx={{ borderRadius: 1 }}
            >
              ยกเลิก
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                const hasText = supplementaryText?.trim();
                const hasImages = supplementaryImages?.length > 0;
                if (supplementaryItem && (hasText || hasImages)) {
                  addSupplementaryInfo(supplementaryItem.id, supplementaryText?.trim() || '', currentUser?.name || '', supplementaryImages || []);
                  setOpenSupplementaryDialog(false);
                  setSupplementaryItem(null);
                  setSupplementaryText('');
                  setSupplementaryImages([]);
                } else {
                  setSnackbarMessage({ severity: 'warning', title: 'ข้อมูลไม่ครบ', text: 'กรุณากรอกข้อมูลเพิ่มเติมหรืออัปโหลดรูปอย่างน้อยหนึ่งรูป' });
                  setSnackbarOpen(true);
                }
              }}
              sx={{
                bgcolor: '#845EC2',
                borderRadius: 1,
                '&:hover': { bgcolor: '#6344A4' }
              }}
            >
              ส่ง
            </Button>
          </DialogActions>
        </Dialog>

      </>
    </Box>
  );
};

export default ReporterDashboard;
